import React, { useState, useEffect, useRef } from 'react';
import DataTable, { DataTableColumn } from "../../../ui/DataTable";
import Swal from 'sweetalert2';
import { EnvelopeIcon, ChevronDownIcon, FunnelIcon } from '@heroicons/react/24/outline';
import { pitchesApi } from '../../../../repositories/pitches';
import { emailApi } from '../../../../repositories/email';
import { emailLogsApi } from '../../../../repositories/emailLogs';
import { handleError } from '../../../../utils/errorHandler';
import EmailPitchModal from '../../../modal/EmailPitchModal';
import { PersonalizedMessage } from '../../../../types/email';
import SettingsPanel from '../../../ui/SettingsPanel';
import Button from '../../../ui/Button';
import StatusBadge from '../../../ui/StatusBadge';

type StatusFilter = 'all' | 'submitted' | 'delivered';

// "All" means both sendable statuses, so the API always receives an explicit list.
const STATUSES_FOR: Record<StatusFilter, string[]> = {
  all: ['submitted', 'delivered'],
  submitted: ['submitted'],
  delivered: ['delivered'],
};

const STATUS_LABELS: Record<StatusFilter, string> = {
  all: 'Submitted and delivered',
  submitted: 'Submitted',
  delivered: 'Delivered',
};

interface SentContactsState {
  names: string[];
  fetched: boolean;
  loading: boolean;
}

const SentContactsIcon: React.FC<{ pitchId: number }> = ({ pitchId }) => {
  const [state, setState] = useState<SentContactsState>({ names: [], fetched: false, loading: false });
  const [visible, setVisible] = useState(false);
  const fetchedRef = useRef(false);

  const handleMouseEnter = async () => {
    setVisible(true);
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    setState((s) => ({ ...s, loading: true }));
    try {
      const res = await emailLogsApi.getEmailLogsByPitch(pitchId);
      const logs: any[] = res?.data?.success ? (res.data.emailLogs ?? []) : [];
      const names = [...new Set(logs.map((l: any) => `${l.contact?.firstName} ${l.contact?.lastName}`.trim() || l.contact?.email))].filter(Boolean) as string[];
      setState({ names, fetched: true, loading: false });
    } catch {
      setState({ names: [], fetched: true, loading: false });
    }
  };

  return (
    <div className="relative inline-flex" onMouseEnter={handleMouseEnter} onMouseLeave={() => setVisible(false)}>
      <button className="p-1 text-neutral-400 hover:text-primary-700 rounded">
        <EnvelopeIcon className="w-4 h-4" />
      </button>
      {visible && (
        <div className="absolute right-0 top-7 z-20 w-48 bg-neutral-800 text-white text-xs rounded-md shadow-lg p-2">
          {state.loading ? (
            <span className="text-neutral-300">Loading...</span>
          ) : state.names.length > 0 ? (
            <>
              <p className="font-medium mb-1 text-neutral-300">Already emailed:</p>
              <ul className="space-y-0.5">
                {state.names.map((name, i) => <li key={i} className="truncate">{name}</li>)}
              </ul>
            </>
          ) : (
            <span className="text-neutral-300">No contacts emailed yet</span>
          )}
        </div>
      )}
    </div>
  );
};

const SendPitch: React.FC = () => {
  const [pitches, setPitches] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPitches, setSelectedPitches] = useState<any[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === pitches.length && pitches.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(pitches.map((p) => p.id)));
    }
  };

  useEffect(() => {
    let cancelled = false;
    const fetchPitches = async () => {
      setLoading(true);
      try {
        const res = await pitchesApi.getAll({ status: STATUSES_FOR[statusFilter], past: false });
        if (!cancelled && res?.data?.success) {
          setPitches(res.data.data ?? []);
        }
      } catch (error) {
        handleError(error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchPitches();
    return () => {
      cancelled = true;
    };
  }, [statusFilter]);

  const handleSend = (pitch: any) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.delete(pitch.id);
      return next;
    });
    setSelectedPitches([pitch]);
    setModalOpen(true);
  };

  const handleGroupedSend = () => {
    const picked = pitches.filter((p) => selectedIds.has(p.id));
    if (picked.length === 0) return;
    setSelectedPitches(picked);
    setModalOpen(true);
  };

  const handleModalSend = async (data: {
    contactIds: any[];
    templateId: any;
    subject: string;
    personalizedMessages?: PersonalizedMessage[];
    scheduledFor?: string;
  }) => {
    if (selectedPitches.length === 0) return;
    const pitchIds = selectedPitches.map((p) => p.id);
    setModalOpen(false);
    setSelectedPitches([]);
    setSelectedIds(new Set());

    Swal.fire({
      title: data.scheduledFor ? 'Scheduling email...' : 'Sending email...',
      allowOutsideClick: false,
      showConfirmButton: false,
      didOpen: () => Swal.showLoading(),
    });

    // Per-recipient notes, in the same { contactId: message } shape the pitch submission
    // endpoint takes. Only sent for templates that contain {{personalizedMessage}}, and only
    // when at least one note was actually written.
    const contactsMap: Record<string, string> = {};
    (data.personalizedMessages ?? []).forEach((m) => {
      if (m.message?.trim()) contactsMap[String(m.contactId)] = m.message;
    });

    try {
      const res = await emailApi.sendEmail({
        pitchIds,
        contactIds: data.contactIds,
        templateId: data.templateId,
        subject: data.subject,
        ...(Object.keys(contactsMap).length > 0 ? { contacts: contactsMap } : {}),
        ...(data.scheduledFor ? { scheduledFor: data.scheduledFor } : {}),
      });
      const scheduledForResp = res?.data?.data?.scheduledFor;
      if (res?.data?.success && scheduledForResp) {
        Swal.fire({
          title: 'Email scheduled!',
          text: `This email will be sent on ${new Date(scheduledForResp).toLocaleString()}.`,
          icon: 'success',
          confirmButtonText: 'OK',
        });
      } else if (res?.data?.success) {
        Swal.fire({ title: 'Email sent!', icon: 'success', confirmButtonText: 'OK' });
      }
      // Refetch so the table reflects the new scheduled/sent state
      const refreshed = await pitchesApi.getAll({ status: STATUSES_FOR[statusFilter], past: false });
      if (refreshed?.data?.success) {
        setPitches(refreshed.data.data ?? []);
      }
    } catch (error) {
      Swal.close();
      handleError(error);
    }
  };

  const getConfig = (pitch: any): string => {
    const configs = Array.isArray(pitch.configuration)
      ? pitch.configuration
      : (pitch.configuration || '').split(',').map((c: string) => c.trim()).filter(Boolean);
    return configs[0] ?? '';
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    const d = new Date(dateString);
    return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`;
  };

  // Selection only matters when there is more than one pitch to group together.
  const showSelection = pitches.length > 1;

  const sendPitchColumns: DataTableColumn<any>[] = [
    ...(showSelection ? [{
      key: "select",
      header: (
        <input
          type="checkbox"
          checked={selectedIds.size === pitches.length && pitches.length > 0}
          onChange={toggleSelectAll}
          className="w-4 h-4 rounded cursor-pointer text-primary-700 border-neutral-300 focus:border-primary-700"
          aria-label="Select all pitches"
        />
      ),
      cell: (pitch) => (
        <input
          type="checkbox"
          checked={selectedIds.has(pitch.id)}
          onChange={() => toggleSelect(pitch.id)}
          className="w-4 h-4 rounded cursor-pointer text-primary-700 border-neutral-300 focus:border-primary-700"
          aria-label={"Select " + (pitch.releaseTitle || "pitch")}
        />
      ),
    }] as DataTableColumn<any>[] : []),
    { key: "releaseTitle", header: "Release Title", cell: (p) => <span className="font-medium">{p.releaseTitle || "-"}</span> },
    { key: "artistName", header: "Artist Name", cell: (p) => <span className="text-neutral-600">{p.artistName || "-"}</span> },
    { key: "configuration", header: "Configuration", cell: (p) => <span className="text-neutral-600">{getConfig(p) || "-"}</span> },
    { key: "releaseDate", header: "Release Date", cell: (p) => <span className="text-neutral-600">{formatDate(p.releaseDate)}</span> },
    { key: "status", header: "Status", cell: (p) => <StatusBadge status={p.status} /> },
    {
      key: "actions",
      header: "Actions",
      cell: (pitch) => (
        <Button
          size="sm"
          onClick={() => handleSend(pitch)}
        >
          Send Email
        </Button>
      ),
    },
  ];

  return (
    <SettingsPanel
      title="Send Pitch"
      description={
        (loading || pitches.length > 0)
          ? `${STATUS_LABELS[statusFilter]} pitches with a future release date.${pitches.length > 1 ? ' Select one or multiple to send a grouped email.' : ''}`
          : undefined
      }
      actions={
        <div className="flex items-center gap-3">
          <div className="relative">
            <FunnelIcon className="absolute w-4 h-4 -translate-y-1/2 pointer-events-none left-3 top-1/2 text-primary-700" />
            <select
              value={statusFilter}
              onChange={(e) => {
                setSelectedIds(new Set());
                setStatusFilter(e.target.value as StatusFilter);
              }}
              className="py-2 pl-9 pr-9 text-sm font-medium text-black transition-colors bg-white border rounded-lg shadow-sm cursor-pointer appearance-none border-neutral-300 hover:border-primary-700 focus:outline-none focus:border-primary-700 focus:ring-2 focus:ring-primary-700/20"
              aria-label="Filter pitches by status"
            >
              <option value="all">All</option>
              <option value="submitted">Submitted</option>
              <option value="delivered">Delivered</option>
            </select>
            <ChevronDownIcon className="absolute w-4 h-4 -translate-y-1/2 pointer-events-none right-3 top-1/2 text-neutral-500" />
          </div>
          {selectedIds.size > 1 && (
            <Button
              onClick={handleGroupedSend}
            >
              Send Grouped Email ({selectedIds.size})
            </Button>
          )}
        </div>
      }
    >
      {!loading && pitches.length === 0 ? (
        <div className="text-center py-12">
          <EnvelopeIcon className="mx-auto h-12 w-12 text-neutral-400" />
          <h3 className="mt-2 text-sm font-medium text-black">
            No {STATUS_LABELS[statusFilter].toLowerCase()} pitches available
          </h3>
          <p className="mt-1 text-sm text-neutral-500">
            Only {STATUS_LABELS[statusFilter].toLowerCase()} pitches with a future release date appear here.
          </p>
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
            <DataTable
              columns={sendPitchColumns}
              rows={pitches}
              getRowKey={(pitch: any) => pitch.id}
              loading={loading}
              emptyMessage={`No ${STATUS_LABELS[statusFilter].toLowerCase()} pitches with a future release date.`}
            />
          </div>

          {/* Mobile list */}
          <div className="md:hidden space-y-4">
            {pitches.map((pitch) => (
              <div key={pitch.id} className="bg-neutral-50 rounded-lg border border-neutral-200 p-4">
                <div className="flex justify-between items-start mb-2">
                  <div className="flex items-start gap-2">
                    {showSelection && (
                      <input
                        type="checkbox"
                        checked={selectedIds.has(pitch.id)}
                        onChange={() => toggleSelect(pitch.id)}
                        className="mt-1 h-4 w-4 text-primary-700 border-neutral-300 rounded focus:border-primary-700"
                        aria-label={`Select ${pitch.releaseTitle || 'pitch'}`}
                      />
                    )}
                    <div>
                      <p className="text-sm font-medium text-black">{pitch.releaseTitle || '-'}</p>
                      <p className="text-xs text-neutral-600">{pitch.artistName || '-'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-neutral-500">{formatDate(pitch.releaseDate)}</span>
                    <SentContactsIcon pitchId={pitch.id} />
                  </div>
                </div>
                <div className="flex items-center justify-between mt-2">
                  <div className="flex items-center gap-2">
                    {getConfig(pitch) && (
                      <span className="px-2 py-0.5 text-xs font-medium bg-primary-100 text-primary-800 rounded-full">
                        {getConfig(pitch)}
                      </span>
                    )}
                    <StatusBadge status={pitch.status} />
                  </div>
                  <Button
                    size="sm"
                    onClick={() => handleSend(pitch)}
                    className="ml-auto"
                  >
                    Send Email
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <EmailPitchModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSelectedPitches([]);
        }}
        onSend={handleModalSend}
        pitchData={selectedPitches.length === 1 ? selectedPitches[0] : undefined}
        pitches={selectedPitches.length > 1 ? selectedPitches : undefined}
        selectedDSPs={
          selectedPitches.length === 1
            ? (selectedPitches[0]?.dsps ?? selectedPitches[0]?.selectedDSPs ?? [])
            : []
        }
        allowScheduling
      />
    </SettingsPanel>
  );
};

export default SendPitch;
